import { Vector3 } from 'three'
import type { SplineType } from '../enums'
import type { Level } from '../levelData'
import { DreamteckSpline } from './dreamteckSpline'

/**
 * Port of `SplineGenerator` (TypeDefIndex 8604) — the road-building pipeline.
 *
 * `SplineGenerator.Initialize(nodeDatas, loopIsClosed, type, reversed)` does,
 * in order:
 *
 *   1. For an open loop, extend the authored node list by one node at each
 *      end, pushed out along the direction the road is already heading. Buses
 *      spawn and despawn on these extensions, off the visible road.
 *   2. `BuildSpline` — the extended node positions become the spline's control
 *      points verbatim (tangents are ignored; every authored tangent in all 40
 *      shipped levels is zero anyway).
 *   3. `SmoothSplineCorners` — resample uniformly by arc length, relax the
 *      sharp corners, rebuild from the relaxed samples.
 *   4. Project the *authored* first and last node onto the finished curve to
 *      get `OriginalStartPercent` / `OriginalEndPercent`, which clip the
 *      visible road mesh so it stops where the level author drew it.
 */

/**
 * `startExtensionDistance` / `endExtensionDistance` are `[SerializeField]`s on
 * the SplineGenerator prefab. IL2CPP keeps the field list but not the values,
 * and the kit has no original Unity bundle to recover them from, so these are
 * tuning placeholders — see docs/PORTING.md.
 */
export interface SplineTuning {
  startExtensionDistance: number
  endExtensionDistance: number
  /** `SplineComputer.sampleRate`; Dreamteck's default is 10. */
  sampleRate: number
  /**
   * Render the road with a different basis than the level declares.
   *
   * All 40 levels declare `SplineType.BSpline`, and `CalculateBSplinePosition`
   * is *not* in the decompiled set (Ghidra covered game code only, not the
   * Dreamteck plugin), so the exact basis cannot be read out of the kit. Two
   * readings survive scrutiny:
   *
   *   - Standard uniform cubic B-spline, which Dreamteck's docs describe and
   *     which does not pass through its control points. Under this basis the
   *     road cuts corners by up to ~10 units and `SmoothSplineCorners` never
   *     fires on any shipped level.
   *   - Something more interpolating. 17 authored corners across the 40 levels
   *     turn more than 100°, which is exactly what the smoothing pass's
   *     curvature threshold is shaped to catch — so under an interpolating
   *     basis that pass does real work.
   *
   * The first is not disqualified by the dead-pass observation, because
   * `SplineGenerator` is also the in-game level editor (`InitializeAsEditor`,
   * `GenerateNode`, `TangentMoved`), so the pass may simply be a guard rail
   * against a designer dragging a node into a hairpin.
   *
   * `npm run verify:boarding` measures the best available discriminator: the
   * `BoardingPoint` child of `WaitingArea.prefab`, at local (0, 0, -7.2), is
   * where a bus pulls up, so it has to land on the 6.5-wide road.
   *
   *     uniform B-spline   mean 2.04  sd 0.70  worst 4.29   38/40 on tarmac
   *     Catmull-Rom        mean 1.51  sd 1.15  worst 6.20   38/40 on tarmac
   *     Linear             mean 1.69  sd 0.71  worst 3.59   39/40 on tarmac
   *
   * That leans uniform — it is what the data declares, its spread is tight,
   * and Catmull-Rom has a 6.2-unit outlier that would park a bus clear of the
   * road. But it does not settle the question: all three land boarding points
   * near the road, and the gaps are within what `busStopForwardOffset` and the
   * extension-distance guess could absorb.
   *
   * So: keep this null, and re-check against the reference clip during tuning.
   * Level 6's 139° corner is where the bases diverge most visibly.
   */
  basisOverride: SplineType | null
}

export const DEFAULT_SPLINE_TUNING: SplineTuning = {
  startExtensionDistance: 12,
  endExtensionDistance: 12,
  sampleRate: 10,
  basisOverride: null,
}

export interface RoadSpline {
  spline: DreamteckSpline
  /** Percent on `spline` where the authored road begins. */
  originalStartPercent: number
  /** Percent on `spline` where the authored road ends. */
  originalEndPercent: number
  smoothing: SmoothingStats
}

const FORWARD = new Vector3(0, 0, 1)

/** Unity's `Vector3.normalized`: below 1e-5 magnitude it returns zero. */
function unityNormalized(v: Vector3, out = new Vector3()): Vector3 {
  const mag = v.length()
  if (mag <= 1e-5) return out.set(0, 0, 0)
  return out.copy(v).divideScalar(mag)
}

/** Unity's `Vector3.Angle`, in radians (the original converts deg→rad right back). */
function angleBetween(a: Vector3, b: Vector3): number {
  const denom = Math.sqrt(a.lengthSq() * b.lengthSq())
  if (denom < 1e-15) return 0
  const cos = Math.max(-1, Math.min(1, a.dot(b) / denom))
  return Math.acos(cos)
}

/** C#'s `Math.Round` — half away from zero is NOT the default; ties go to even. */
function roundHalfToEven(value: number): number {
  const floor = Math.floor(value)
  const frac = value - floor
  if (frac > 0.5) return floor + 1
  if (frac < 0.5) return floor
  return floor % 2 === 0 ? floor : floor + 1
}

export function buildRoadSpline(
  level: Level,
  tuning: SplineTuning = DEFAULT_SPLINE_TUNING,
): RoadSpline {
  const authored = level.nodes.map((n) => n.position.clone())
  if (authored.length < 2) {
    throw new Error(`Level ${level.number} has ${authored.length} node(s); need at least 2`)
  }

  const closed = level.splineIsClosed
  const basis = tuning.basisOverride ?? level.splineType
  let controlPoints = authored.map((p) => p.clone())

  // 1. Extension nodes (open splines only).
  if (!closed) {
    const last = authored.length - 1

    const startDir = unityNormalized(new Vector3().subVectors(authored[0], authored[1]))
    if (startDir.lengthSq() === 0) startDir.copy(FORWARD)
    const prepend = authored[0]
      .clone()
      .add(startDir.multiplyScalar(tuning.startExtensionDistance))

    const endDir = unityNormalized(new Vector3().subVectors(authored[last], authored[last - 1]))
    if (endDir.lengthSq() === 0) endDir.copy(FORWARD)
    const append = authored[last].clone().add(endDir.multiplyScalar(tuning.endExtensionDistance))

    controlPoints = [prepend, ...controlPoints, append]
  }

  // 2 + 3. Build, then smooth the corners (which rebuilds from the samples).
  const smoothing: SmoothingStats = {
    skipped: false,
    resampleCount: 0,
    sweepsRun: 0,
    relaxations: 0,
    peakExcess: 0,
  }
  let spline = new DreamteckSpline(controlPoints, basis, closed, tuning.sampleRate)
  const smoothed = smoothSplineCorners(spline, closed, smoothing)
  if (smoothed) {
    spline = new DreamteckSpline(smoothed, basis, closed, tuning.sampleRate)
  }

  // `Initialize` reverses before projecting, so the percents below already
  // refer to the reversed curve.
  if (level.splineReversed) {
    // `ApplyReverseSpline` is 16 KB of inlined Dreamteck point-array juggling
    // in the decompile; a plain order reversal is the sane reading, but it is
    // not verified. Only levels 25 and 39 set `SplineReversed`.
    spline = new DreamteckSpline(
      [...spline.points].reverse(),
      basis,
      closed,
      tuning.sampleRate,
    )
  }

  // 4. Clip range for the visible road.
  const a = spline.project(authored[0])
  const b = spline.project(authored[authored.length - 1])
  return {
    spline,
    originalStartPercent: Math.min(a, b),
    originalEndPercent: Math.max(a, b),
    smoothing,
  }
}

/**
 * Port of `SplineGenerator.SmoothSplineCorners`. Returns the new control
 * points, or `null` when the original bails out (curve shorter than 12 units).
 *
 * The relaxation is Gauss-Seidel — each point is written back immediately, so
 * the next point in the same sweep sees the updated neighbour. Do not
 * double-buffer this; it changes the result.
 */
export interface SmoothingStats {
  /** Curve was shorter than 12 units, so the original bails out entirely. */
  skipped: boolean
  resampleCount: number
  sweepsRun: number
  /** How many point relaxations fired in total across all sweeps. */
  relaxations: number
  /** Highest curvature excess seen on the first sweep. */
  peakExcess: number
}

export function smoothSplineCorners(
  spline: DreamteckSpline,
  closed: boolean,
  stats?: SmoothingStats,
): Vector3[] | null {
  if (stats) {
    stats.skipped = false
    stats.resampleCount = 0
    stats.sweepsRun = 0
    stats.relaxations = 0
    stats.peakExcess = 0
  }

  const length = spline.calculateLength(0, 1)
  if (length < 12) {
    if (stats) stats.skipped = true
    return null
  }

  // One resample interval per ~2 world units, never fewer than 6.
  let intervals = roundHalfToEven(length * 0.5)
  if (intervals < 7) intervals = 6

  const count = intervals + (closed ? 0 : 1)
  const samples: Vector3[] = new Array(count)
  for (let i = 0; i < count; i++) {
    const percent = spline.travel(0, (length * i) / intervals, true)
    samples[i] = spline.evaluatePosition(percent)
  }

  // Curvature above 1/1.15 rad per world unit gets pulled toward the midpoint
  // of its neighbours, until nothing exceeds the threshold by 0.02 or 60
  // sweeps have run.
  const CURVATURE_THRESHOLD = 0.86956525
  const SETTLE_EPSILON = 0.02
  const MAX_SWEEPS = 60

  const first = closed ? 0 : 1
  const lastExclusive = closed ? count : count - 1
  const d1 = new Vector3()
  const d2 = new Vector3()

  if (stats) stats.resampleCount = count

  for (let sweep = 0; sweep < MAX_SWEEPS; sweep++) {
    let maxExcess = 0
    if (stats) stats.sweepsRun = sweep + 1
    for (let i = first; i < lastExclusive; i++) {
      const prev = samples[(i - 1 + count) % count]
      const cur = samples[i]
      const next = samples[(i + 1) % count]

      const avgLen = (prev.distanceTo(cur) + cur.distanceTo(next)) * 0.5
      if (avgLen < 0.0001) continue

      unityNormalized(d1.subVectors(cur, prev), d1)
      unityNormalized(d2.subVectors(next, cur), d2)
      const curvature = angleBetween(d1, d2) / avgLen
      const excess = curvature - CURVATURE_THRESHOLD
      if (stats && sweep === 0 && excess > stats.peakExcess) stats.peakExcess = excess
      if (excess <= 0) continue
      if (stats) stats.relaxations++

      const t = Math.min(avgLen * excess, 1) * 0.5
      cur.set(
        cur.x + ((prev.x + next.x) * 0.5 - cur.x) * t,
        cur.y + ((prev.y + next.y) * 0.5 - cur.y) * t,
        cur.z + ((prev.z + next.z) * 0.5 - cur.z) * t,
      )
      if (excess > maxExcess) maxExcess = excess
    }
    if (maxExcess < SETTLE_EPSILON) break
  }

  return samples
}
